import React, { Component } from "react"
import Helmet from "react-helmet"
import numeral from "numeral"
import styled, { css } from "styled-components"
import {
  TERTIARY_TEXT,
  SECONDARY_TEXT,
  PRIMARY_TEXT,
  SMALLCHAT_SHADOW,
  SMALLCHAT_PURPLE
} from "../components/consts/colors"
import AddtoSlack from "../components/AddtoSlack"
import LogoMarquee from "../components/LogoMarquee"
import SectionBackground from "../components/SectionBackground"
import pricingImage from "../assets/svgs/PricingBackground.svg"
import logosOne from "../assets/logosOne.png"
import logosTwo from "../assets/logosTwo.png"
import withScreensize from "../components/withScreensize"
import SectionCallout from "../components/SectionCallout"
import SectionCalloutBackground from "../assets/svgs/SectionCalloutBackground.svg"

const PricingContainer = styled.div`
  overflow: hidden;
`

const PricingHeader = styled.header`
  position: relative;
  z-index: 10;
  text-align: center;
  padding: 50px 50px 0;
  h1 {
    display: inline-block;
  }
`

const toggleHover = css`
  &:after {
    content: "";
    position: absolute;
    top: 100%;
    left: 10%;
    display: block;
    width: 80%;
    height: 2px;
    background-color: ${SMALLCHAT_PURPLE};
  }
`

const PricingToggle = styled.button`
  position: relative;
  outline: none;
  border: none;
  background: none;
  padding: 0;
  margin: 30px 20px 20px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 900;
  font-family: "Rubik";
  color: ${props => (props.selected ? PRIMARY_TEXT : TERTIARY_TEXT)};
  cursor: pointer;
  ${props => props.selected && toggleHover};
  &:hover {
    color: ${PRIMARY_TEXT};
    ${toggleHover};
  }
`

const PricingWrapper = styled.div`
  z-index: 2;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 50px;
  width: 100%;
  @media (max-width: 480px) {
    padding: 0 20px;
  }
`

const PricingPlans = styled.div`
  z-index: 2;
  position: relative;
  display: flex;
  padding: 50px 0;
  margin-bottom: 100px;
  background-color: white;
  border-radius: 50px;
  box-shadow: 0 0 20px 0 ${SMALLCHAT_SHADOW};
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 50px;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    padding: 0 20px;
  }
`

const PlanContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  @media (max-width: 768px) {
    padding: 50px 0;
  }
  & + &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 1px;
    height: 100%;
    background-color: ${TERTIARY_TEXT};
    @media (max-width: 768px) {
      height: 1px;
      width: 100%;
    }
  }
`

const PlanPrice = styled.div`
  margin: 10px 0;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-family: "Rubik";
  h1 {
    margin: 0 5px;
    font-size: 80px;
    line-height: 70px;
    @media (max-width: 480px) {
      font-size: 60px;
    }
  }
  span {
    align-self: flex-start;
  }
`

const PlanLabel = styled.h4`
  font-family: "Lato";
  font-weight: 500;
  font-size: 21px;
  color: ${SECONDARY_TEXT};
`

const PlanFeatures = styled.ul`
  margin: 20px 0;
  padding: 0;
  min-height: 100px;
  list-style-type: none;
  li {
    font-size: 15px;
    line-height: 24px;
    ${props =>
      props.selected &&
      css`
        color: #9192c5;
        font-weight: 600;
      `};
  }
`

const PlanPrompt = styled(AddtoSlack)`
  margin: 15px 0;
`

const PricingValidation = styled.div`
  z-index: 2;
  position: relative;
  text-align: center;
  margin: 50px 0 100px;
  h2 {
    margin-bottom: 30px;
  }
`

const PricingPlan = ({
  index,
  interval,
  activeIndex,
  onSelect,
  planPrice,
  planLabel,
  featureList
}) => {
  const priceForInterval = planPrice * (interval === 12 ? 0.8 : 1)
  const [dollars, cents] = numeral(priceForInterval)
    .format("0.00")
    .split(".")
  return (
    <PlanContainer onMouseOver={e => onSelect(index)}>
      <PlanPrice>
        $<h1>{dollars}</h1>
        <span>{cents}</span>
      </PlanPrice>
      <p
        style={{
          position: "relative",
          top: "-5px",
          fontSize: "13px",
          color: "#B3B3BF"
        }}
      >
        per month
      </p>
      <PlanLabel>{planLabel}</PlanLabel>
      <PlanFeatures selected={activeIndex >= index}>
        {featureList.map((feature, index) => <li key={index}>- {feature}</li>)}
      </PlanFeatures>
      <PlanPrompt />
    </PlanContainer>
  )
}

class Pricing extends Component {
  state = {
    interval: 1,
    activeIndex: null
  }

  selectPlan = index => {
    if (index !== this.state.activeIndex) {
      this.setState({ activeIndex: index })
    }
  }

  setInterval = interval => {
    this.setState({ interval })
  }

  render() {
    const isMobile = this.props.screensize === "phone"
    const isTablet = this.props.screensize === "tablet"
    return (
      <PricingContainer>
        <Helmet title="Smallchat Pricing" />
        <PricingHeader>
          <h1>Start using Smallchat for free.</h1>
          <p>And then keep using it for free if you'd like.</p>
          <PricingToggle
            selected={this.state.interval === 12}
            onClick={e => this.setInterval(12)}
          >
            Annually
          </PricingToggle>
          <PricingToggle
            selected={this.state.interval === 1}
            onClick={e => this.setInterval(1)}
          >
            Monthly
          </PricingToggle>
        </PricingHeader>
        <PricingWrapper>
          <SectionBackground
            background={pricingImage}
            backgroundWidth="140%"
            backgroundOrigin="top"
            backgroundHeight="130%"
            backgroundScale={isMobile ? 5 : isTablet ? 2 : 1.2}
            backgroundTop={isMobile || isTablet ? 0 : "-20%"}
            backgroundRight="-40%"
          />
          <PricingPlans>
            <PricingPlan
              {...this.state}
              index={0}
              onSelect={this.selectPlan}
              planPrice={0}
              planLabel="Personal 👶"
              featureList={[
                "Unlimited conversations",
                "Unlimited operators",
                "10k/mo pageviews",
                "Full customization of your widget"
              ]}
            />
            <PricingPlan
              {...this.state}
              index={1}
              onSelect={this.selectPlan}
              planPrice={19.99}
              planLabel="Growth 😎"
              featureList={[
                "All Personal tier features plus",
                "Custom auto messages",
                "Contact list with export",
                "Unlimited pageviews"
              ]}
            />
            <PricingPlan
              {...this.state}
              index={2}
              onSelect={this.selectPlan}
              planPrice={39.99}
              planLabel="Business 💼"
              featureList={[
                "All Personal + Growth tier features plus",
                "Multiple Slack channels + widgets",
                "File uploads",
                "Whitelabel chat widget",
                "Javascript API"
              ]}
            />
          </PricingPlans>
        </PricingWrapper>
        <PricingValidation>
          <h2>Trusted by over 5000 teams!</h2>
          <LogoMarquee src={logosOne} speed={35000} />
          <LogoMarquee src={logosTwo} speed={40000} />
        </PricingValidation>
        <SectionCallout
          darkShadow
          background={SectionCalloutBackground}
          backgroundSize="cover"
          backgroundPosition="80% 80%"
          title="Start your conversations today."
        />
      </PricingContainer>
    )
  }
}

export default withScreensize(Pricing)



// WEBPACK FOOTER //
// ./src/pages/pricing.js